/**
 * GTM 事件追踪方法
 * 
 * 通过 Google Tag Manager (GTM) 统一管理所有广告平台的追踪：
 * - Google Ads
 * - Facebook/Meta Ads
 * - TikTok Ads
 * - 其他平台
 */

import { pushEvent } from './gtm'

/**
 * 页面浏览事件
 * @param {string} pagePath - 页面路径
 * @param {string} pageTitle - 页面标题
 */
export function trackPageView(pagePath, pageTitle) {
  pushEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle
  })
}

/**
 * 用户登录事件
 * @param {string} userId - 用户ID（如 email）
 * @param {string} method - 登录方式（如 'telegram', 'email'）
 */
export function trackLogin(userId, method = 'telegram') {
  pushEvent('login', {
    user_id: userId,
    method: method
  })
}

/**
 * 用户注册事件（GA4 标准事件）
 * @param {string} userId - 用户ID（如 email）
 * @param {string} method - 注册方式（如 'email', 'telegram'）
 */
export function trackSignUp(userId, method = 'email') {
  pushEvent('sign_up', {
    user_id: userId,
    method: method
  })
}

/**
 * 查看商品事件
 * @param {object} product - 商品信息
 */
export function trackViewItem(product) {
  pushEvent('view_item', {
    currency: 'USD',
    value: product.priceUSD || product.price,
    items: [{
      item_id: product.id || product.productId,
      item_name: product.name || product.displayName,
      item_category: product.country || product.countryName,
      price: product.priceUSD || product.price,
      quantity: 1
    }]
  })
}

/**
 * 加入购物车事件
 * @param {object} product - 商品信息
 * @param {number} quantity - 数量
 */
export function trackAddToCart(product, quantity = 1) {
  pushEvent('add_to_cart', {
    currency: 'USD',
    value: (product.priceUSD || product.price) * quantity,
    items: [{
      item_id: product.id || product.productId,
      item_name: product.name || product.displayName,
      item_category: product.country || product.countryName,
      price: product.priceUSD || product.price,
      quantity: quantity
    }]
  })
}

/**
 * 开始结算/交易事件
 * @param {object} orderInfo - 订单信息
 */
export function trackBeginCheckout(orderInfo) {
  const items = orderInfo.items || [{
    item_id: orderInfo.productId,
    item_name: orderInfo.productName,
    price: orderInfo.priceUSD,
    quantity: 1
  }]

  pushEvent('begin_checkout', {
    currency: 'USD',
    value: orderInfo.totalUSD || orderInfo.priceUSD,
    items: items
  })
}

/**
 * 购买成功事件 - 核心转化事件（仅后端使用）
 * @param {object} order - 订单信息
 */
export function trackPurchase(order) {
  const items = order.items || [{
    item_id: order.productId,
    item_name: order.productName || order.goodsName,
    item_category: order.country || order.countryName,
    price: order.priceUSD || order.price,
    quantity: order.quantity || 1
  }]

  pushEvent('purchase', {
    // 标准电商参数
    transaction_id: order.orderNo,
    value: order.priceUSD || order.totalUSD || order.price,
    currency: 'USD',
    
    // 商品信息
    items: items,
    
    // 额外参数（可用于投放分析）
    payment_method: order.paymentMethod,
    coupon: order.couponCode || '',
    
    // 自定义参数（可在 GTM 中使用）
    order_no: order.orderNo,
    product_name: order.productName || order.goodsName,
    country: order.country || order.countryName
  })
}

/**
 * 自定义事件
 * @param {string} eventName - 事件名称
 * @param {object} params - 事件参数
 */
export function trackCustomEvent(eventName, params = {}) {
  pushEvent(eventName, params)
}
